import { Link } from '@tanstack/react-router'
import type { ComponentProps } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

/** Internal `/path` links navigate client-side; external links open safely.
 *  A protocol-relative `//host` href is NOT internal — treat it as external
 *  so it can't ride the client-side-navigation branch. */
function MarkdownLink({
  // react-markdown passes the mdast `node` to custom components; it must not
  // reach the DOM (it would render as node="[object Object]").
  node: _node,
  href = '',
  children,
  ...rest
}: ComponentProps<'a'> & { node?: unknown }) {
  if (href.startsWith('/') && !href.startsWith('//')) {
    // `to` is a typed route union; a Markdown href is a plain string.
    return (
      <Link to={href as never} {...rest}>
        {children}
      </Link>
    )
  }
  const external = /^https?:/.test(href) || href.startsWith('//')
  return (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
      {...rest}
    >
      {children}
    </a>
  )
}

/**
 * Behavior-only overrides (the successor to the MDX pipeline's
 * mdx-components): client-side routing for internal links, and a horizontally
 * scrollable wrapper so wide GFM tables don't overflow on mobile. Prose
 * styling comes from the `.prose` container the callers wrap this in.
 */
const components: Components = {
  a: MarkdownLink,
  table: ({ node: _node, ...props }) => (
    <div className="overflow-x-auto">
      <table {...props} />
    </div>
  ),
}

/**
 * Render CMS markdown (a `cms_documents.body_md` value) at request time.
 * GFM enabled to match what the build-time MDX pipeline supported.
 *
 * Two deliberate contracts:
 * - Markdown ONLY. Raw inline HTML in a body is dropped (react-markdown's
 *   default, no rehype-raw on purpose) — the admin editor says so, and the
 *   preview tab shows the same rendering, so authors see the drop before
 *   publishing.
 * - Runtime parsing (server per uncached render + client on hydration) is an
 *   accepted trade for keeping one renderer everywhere including the admin
 *   preview; the public pages sit behind swr edge caching, which absorbs the
 *   server-side cost. Revisit with a server-side HTML pipeline only if the
 *   client bundle size actually becomes a problem.
 */
export function Markdown({ children }: { children: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children}
    </ReactMarkdown>
  )
}
